import { useColorModeValue } from "@chakra-ui/react";

// The SMS Spend charts' colours, by the job each does (the data-viz reference palette, validated
// with its checker in both modes: the categorical slots on adjacent pairs, the blue steps as an
// ordinal ramp). Three light-mode slots sit under 3:1 on the surface, so every chart has a legend
// and a table view.

const LIGHT = {
  surface: "#fcfcfb",
  text: "#0b0b0b",
  textSecondary: "#52514e",
  muted: "#898781",
  grid: "#e1e0d9",
  baseline: "#c3c2b7",
  border: "rgba(11,11,11,0.10)",
  hover: "rgba(11,11,11,0.04)",
  series: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300"],
  other: "#b9b8b1",
  // Ordinal (text length 1 → 11+ segments) and sequential (the heatmap), one hue, light → dark.
  ordinal: ["#86b6ef", "#5598e7", "#2a78d6", "#1c5cab", "#104281"],
  sequential: ["#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#184f95", "#0d366b"],
  empty: "#f0efec",
};

const DARK = {
  surface: "#1a1a19",
  text: "#f0efec",
  textSecondary: "#c3c2b7",
  muted: "#898781",
  grid: "#2c2c2a",
  baseline: "#383835",
  border: "rgba(255,255,255,0.10)",
  hover: "rgba(255,255,255,0.06)",
  series: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300"],
  other: "#5d5c57",
  ordinal: ["#184f95", "#256abf", "#3987e5", "#6da7ec", "#9ec5f4"],
  sequential: ["#104281", "#184f95", "#1c5cab", "#256abf", "#2a78d6", "#5598e7", "#86b6ef"],
  empty: "#262624",
};

// Status: reserved meaning, always shown with an icon and words.
export const STATUS = { good: "#0ca30c", warning: "#fab219", serious: "#ec835a", critical: "#d03b3b" };

export function useVizTokens() {
  return useColorModeValue(LIGHT, DARK);
}

// Why a text went out, folded into the six families the spend-over-time stack shows (plus Other);
// "Received" is the texts patients sent, shown when the direction filter includes them. A family
// keeps its colour slot whatever the counts: colour follows the family, never its rank.
export const FAMILIES = [
  { key: "reminders", label: "Reminders & confirmations",
    sources: ["reminder", "confirmation", "auto_confirmation", "scheduled"] },
  { key: "forms", label: "Form links", sources: ["forms"] },
  { key: "welcome", label: "Welcome texts", sources: ["welcome"] },
  { key: "staff", label: "Typed or resent by staff", sources: ["manual", "resend"] },
  { key: "replies", label: "Automatic replies, AI and agents", sources: ["auto_reply", "ai_chat", "agent"] },
  { key: "received", label: "Received from patients", sources: ["received"] },
  { key: "other", label: "Retries, sign-in codes and other", sources: ["auto_resend", "verification", "other"] },
];

const FAMILY_OF = Object.fromEntries(FAMILIES.flatMap((f) => f.sources.map((s) => [s, f.key])));

export function familyOf(source) {
  return FAMILY_OF[source] || "other";
}

export function familyColor(tokens, key) {
  const i = FAMILIES.findIndex((f) => f.key === key);
  return i >= 0 && i < tokens.series.length ? tokens.series[i] : tokens.other;
}
